import type { DistanceTripsQuery, Page } from '@chukua/common';
import { Button, Table } from 'antd';
import { CircleCheck, SearchX } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { DistanceTripItem } from '../distance-agreement.api';
import type { CloseOutTab } from '../distance-agreement.paths';
import { buildCloseOutColumns } from './build-close-out-columns';

interface CloseOutTableProps {
  tab: CloseOutTab;
  page: Page<DistanceTripItem>;
  list: ListQueryControls<DistanceTripsQuery>;
  isRefreshing: boolean;
}

const EMPTY_TITLES: Record<CloseOutTab, string> = {
  gaps: 'No open distance gaps',
  resolved: 'No resolved gaps yet',
  all: 'No trips closed out yet',
};

const EMPTY_DESCRIPTIONS: Record<CloseOutTab, string> = {
  gaps: 'Nothing is flagged. A trip appears here when its odometer and GPS distances differ by more than the tolerance.',
  resolved: 'A gap appears here once someone has recorded how it was settled.',
  all: 'A trip appears here once its truck has been received at the yard.',
};

/** Search box plus the server-paged table of closed-out trips; the old rows stay up, dimmed, while the next page loads. */
export function CloseOutTable({ tab, page, list, isRefreshing }: CloseOutTableProps) {
  if (page.total === 0 && !list.isFiltered) {
    return <EmptyState icon={CircleCheck} title={EMPTY_TITLES[tab]} description={EMPTY_DESCRIPTIONS[tab]} />;
  }

  const truckFilter = list.query.truckId;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <ListSearch value={list.query.q} placeholder="Search trip or truck plate" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        {truckFilter && truckFilter.length > 0 && (
          <Button size="small" onClick={() => list.setQuery({ truckId: [] })}>
            Showing one truck. Show all trucks
          </Button>
        )}
      </div>
      <Table
        rowKey="tripId"
        dataSource={page.items}
        columns={buildCloseOutColumns(list, { hasStateFilter: tab === 'all' })}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={list.isFiltered ? { emptyText: <NoMatchingTrips /> } : undefined}
        {...list.tableProps<DistanceTripItem>(page.total)}
      />
    </div>
  );
}

function NoMatchingTrips() {
  return <EmptyState icon={SearchX} title="No matching trips" description="No closed-out trip matches this search or these filters." />;
}
